/**
 * The single action button every game uses (Spin / Pull / Drop / Kick / Draw).
 * Replaces the 8 near-identical `rounded-full px-8 py-3 …` blocks that drifted
 * in casing, emoji and disabled styling. Accent-filled, brand-readable text,
 * tactile press, accessible busy state — no emojis.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { readableText, shade, tint, withAlpha } from './colors';

interface GameButtonProps {
  accent: string;
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  type?: 'button' | 'submit';
}

export function GameButton({
  accent,
  busy = false,
  disabled = false,
  onClick,
  children,
  className,
  type = 'button',
}: GameButtonProps) {
  const isOff = busy || disabled;
  const fg = readableText(accent);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isOff}
      aria-busy={busy}
      className={cn(
        'relative inline-flex items-center justify-center gap-2 rounded-full px-9 py-3.5',
        'text-base font-extrabold uppercase tracking-wide',
        'transition-[transform,box-shadow,filter] duration-150 outline-none',
        'focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        isOff ? 'cursor-default opacity-80' : 'cursor-pointer hover:scale-[1.04] active:scale-95',
        className,
      )}
      style={{
        color: fg,
        background: `linear-gradient(180deg, ${tint(accent, 0.18)} 0%, ${accent} 55%, ${shade(
          accent,
          0.82,
        )} 100%)`,
        boxShadow: isOff
          ? 'none'
          : `0 6px 18px ${withAlpha(accent, 0.5)}, inset 0 1px 0 ${withAlpha('#ffffff', 0.35)}`,
      }}
    >
      {busy && (
        <span
          className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden
        />
      )}
      <span>{children}</span>
    </button>
  );
}
